import * as React from 'react'
import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { useMatch } from 'react-router-dom'
import { parseAsString, useQueryState } from 'nuqs'

import { clsx } from 'clsx'

import {
  XIcon,
  CommentSquare1StIcon,
  Dialog,
  Flex,
  IconButton,
  MapPinIcon,
  NoteBlankIcon,
  PinIcon,
  RepeatIcon,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import { PortalContainerProvider } from '@buffer-mono/popcorn/src/helpers/usePortalContainer'

import { getAnalyzeUrl } from '~publish/components/PostCard/helpers'
import { PostCardActions } from '~publish/components/PostCard/PostCardActions'
import { PostCardAttachments } from '~publish/components/PostCard/PostCardAttachments'
import {
  useMetadataFirstComment,
  useMetadataGeolocation,
  useMetadataThreadCount,
  useMetadataTitle,
  usePostData,
} from '~publish/components/PostCard/PostCardContext'
import { PostCardProcessingNotice } from '~publish/components/PostCard/PostCardProcessingNotice'
import { PostMediaBentoBoxPreview } from '~publish/components/PostCard/PostMedia'
import { PostMediaAsset_Asset } from '~publish/components/PostCard/PostMedia/PostMediaAsset'
import { getEntriesWithKeys } from '~publish/helpers/getEntriesWithKeys'
import { usePostRecurrenceInfo } from '~publish/components/PostCard/usePostRecurrenceInfo'
import { PostCardContent } from '~publish/components/PostCardContent'
import { PostCardContent_Annotation } from '~publish/components/PostCardContent/PostCardContent'
import type { PostCardContent_AnnotationData } from '~publish/components/PostCardContent/PostCardContent'
import { PostCardAuthor } from '~publish/components/PostCardFooter/PostCardAuthor'
import { PostedFrom } from '~publish/components/PostCardFooter/PostedFrom'
import { PostCardHeader } from '~publish/components/PostCardHeader'
import { PostCardTags } from '~publish/components/PostCardTags'
import {
  InsightsIconButton,
  MetricsRefresh,
  MoreAnalyticsButton,
  PostMetricsScroller,
  PostMetricsWrapper,
} from '~publish/components/PostDetails/PostMetrics'
import { PostNotifyBadge } from '~publish/components/PostNotifyBadge'
import {
  NotesIndicator,
  getNotesIndicatorLabel,
  getNotesUnreadTooltip,
} from '~publish/components/Notes'
import { PostStatusBadge } from '~publish/components/PostStatusBadge'
import type { PostType } from '~publish/components/graphqlEnums'
import { readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { getReadablePostStatus } from '~publish/helpers/posts/getReadablePostStatus'
import { useOptionalNotesDismissGuard } from '~publish/hooks/notes/NotesDismissGuard'
import {
  usePostUnreadMentionCount,
  usePostUnreadNoteCount,
} from '~publish/hooks/notes/usePostNoteCounts'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'
import { useCanShowAnalyzeFallback } from '~publish/hooks/useAnalyzeSunsetEnabled'
import { useGetEngagementUrl } from '~publish/hooks/useGetEngagementUrl'
import { useTimezone } from '~publish/hooks/useTimezone'
import { insightsChannel } from '~publish/legacy/routes'
import { isChannelSupportedByInsights } from '~publish/pages/Insights/utils'

import { PostCard_Post, PostCardProvider } from '../../PostCard'

import { PostDetailsConnectionNotice } from './PostDetailsConnectionNotice'
import { PostDetailsNotes } from './PostDetailsNotes'
import { useCollaborationNotesPanel } from './useCollaborationNotesPanel'

import styles from './PostDetailsContent.module.css'

// TODO: we have this same component in PostCardHeader, we should move it to a shared component
const TextSeparator = (): JSX.Element => (
  <Text color="subtle" aria-hidden={true}>
    {' • '}
  </Text>
)

const CustomScheduleLabel = (): JSX.Element => {
  return (
    <Text size="sm" color="subtle" className={styles.customLabel}>
      <TextSeparator />
      <Tooltip content="Posting time was set manually and is not determined by the channel’s posting schedule">
        <Flex gap="2xs" align="center">
          <PinIcon size="xsmall" />
          <span>Custom</span>
        </Flex>
      </Tooltip>
    </Text>
  )
}

const RecurringPostLabel = ({ summary }: { summary?: string }): JSX.Element => {
  return (
    <Text size="sm" color="subtle" className={styles.customLabel}>
      <TextSeparator />
      <Tooltip content={summary}>
        <Flex gap="2xs" align="center">
          <RepeatIcon size="xsmall" />
          Recurring
        </Flex>
      </Tooltip>
    </Text>
  )
}

const Header = ({ children }: { children?: React.ReactNode }): JSX.Element => {
  const {
    dueAt,
    channel,
    status,
    isCustomScheduled,
    sentAt,
    schedulingType,
    notificationStatus,
  } = usePostData()
  const dateTimeFormatter = useDateTimeFormatter()
  const timeZone = useTimezone()
  const recurrenceInfo = usePostRecurrenceInfo()

  const date = status === 'sent' && sentAt ? sentAt : dueAt

  return (
    <Dialog.Header className={styles.header}>
      <VisuallyHidden>
        <Dialog.Title>View Post details</Dialog.Title>
        <Dialog.Description>
          {`Post ${getReadablePostStatus(status)} for ${channel.name} ${
            channel.service
          } channel ${dueAt ? `on ${dateTimeFormatter(dueAt)}` : ''}`}
        </Dialog.Description>
      </VisuallyHidden>
      <Flex gap="xs" align="center" className={styles.headerLead}>
        {date && (
          <Text>
            {sentAt ? 'Published on' : 'Scheduled for'}&nbsp;
            <Tooltip
              content={
                <Flex direction="column">
                  {timeZone && <span>Channel Local Time:</span>}
                  {dateTimeFormatter(
                    date,
                    timeZone
                      ? {
                          timeZone,
                        }
                      : {},
                  )}
                </Flex>
              }
            >
              <Text size="md" weight="medium">
                {dateTimeFormatter(date, { includeTimeZone: false })}
              </Text>
            </Tooltip>
            {isCustomScheduled && <CustomScheduleLabel />}
          </Text>
        )}
        {recurrenceInfo && (
          <RecurringPostLabel summary={recurrenceInfo.summary} />
        )}
        <PostStatusBadge />
        <PostNotifyBadge
          schedulingType={schedulingType}
          status={status}
          notificationStatus={notificationStatus}
        />
      </Flex>
      <Flex gap="xs" className={styles.headerActions}>
        {children}
        <Dialog.Close asChild>
          <IconButton
            data-post-details-close=""
            label="Close"
            onClick={undefined}
            size="medium"
            variant="tertiary"
          >
            <XIcon />
          </IconButton>
        </Dialog.Close>
      </Flex>
    </Dialog.Header>
  )
}

const BodySection = ({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}): JSX.Element => {
  return (
    <Flex
      as="section"
      className={clsx(styles.bodySection, className)}
      display="flex"
      direction="column"
      justify="between"
      gap="md"
      align="stretch"
    >
      {children}
    </Flex>
  )
}

const PostContent = ({
  text,
  annotations,
  assets,
  title,
  type,
  threadIndex,
  expandText = false,
  includeMedia = true,
}: {
  text: string
  annotations: readonly PostCardContent_AnnotationData[]
  assets: FragmentOf<typeof PostMediaAsset_Asset>[]
  title?: string
  type?: PostType
  threadIndex?: number
  expandText?: boolean
  includeMedia?: boolean
}): JSX.Element => {
  const isThreaded = typeof threadIndex === 'number' && threadIndex >= 0
  return (
    <Flex justify="between" gap="md" align="stretch" fullWidth>
      <Flex
        direction="column"
        align="stretch"
        gap="sm"
        className={styles.leftContent}
      >
        <PostCardHeader type={type} />
        {isThreaded ? (
          <Flex className={styles.threadedContentWrapper} gap="lg">
            <div className={styles.threadedTimelineMarker}></div>
            <Flex direction="column" gap="sm">
              <PostCardContent
                lineClamp={7}
                title={title}
                text={text}
                annotations={annotations ?? []}
                expanded={expandText}
                className={styles.threadText}
              />
              {includeMedia && (
                <>
                  <PostCardAttachments includeRetweet={threadIndex === 0} />
                  <PostMediaBentoBoxPreview assets={assets} />
                </>
              )}
            </Flex>
          </Flex>
        ) : (
          <>
            <PostCardContent
              lineClamp={7}
              title={title}
              text={text}
              annotations={annotations ?? []}
              expanded={expandText}
            />
            {includeMedia && (
              <>
                <PostCardAttachments />
                <PostMediaBentoBoxPreview assets={assets} />
              </>
            )}
          </>
        )}
      </Flex>
    </Flex>
  )
}

const Metrics = (): JSX.Element | null => {
  const { metrics, channel, id: postId } = usePostData()

  const canShowAnalyzeFallback = useCanShowAnalyzeFallback()

  const engageUrl = useGetEngagementUrl(channel, postId)
  const analyzeUrl = getAnalyzeUrl(channel)

  // When this dialog is opened from the Insights tab, the user is already on
  // Insights, so we hide the CTA entirely rather than sending them out to
  // Analyze (a worse experience) or back to a page they're on. See CORE-5616.
  const isOnInsights = !!useMatch(insightsChannel.postsRoute)

  const shouldShowAnalyticsCTA =
    canShowAnalyzeFallback &&
    (channel.service === 'linkedin' ? channel.type === 'page' : true)
  const shouldShowInsightsCTA = isChannelSupportedByInsights(
    channel.service,
    channel.type,
  )

  const renderCta = (): JSX.Element | null => {
    if (isOnInsights) return null
    // Mirror PostCardMetrics: point supported channels at the in-app Insights
    // channel page, and only fall back to Analyze for the ones Insights does
    // not cover.
    if (shouldShowInsightsCTA) {
      return <InsightsIconButton insightsUrl={`/insights/${channel.id}`} />
    }
    if (shouldShowAnalyticsCTA && analyzeUrl) {
      return <MoreAnalyticsButton analyzeUrl={analyzeUrl} />
    }
    return null
  }

  return (
    <PostMetricsWrapper className={styles.dialogMetrics}>
      <PostMetricsScroller metrics={metrics ?? []} engageUrl={engageUrl} />
      {renderCta()}
      <MetricsRefresh />
    </PostMetricsWrapper>
  )
}

const Body = (): JSX.Element => {
  const {
    assets,
    metadata,
    schedulingType,
    status,
    sentAt,
    text,
    tags,
    allowedActions,
  } = usePostData()
  const [expandText] = useQueryState('expandText', parseAsString)
  const showMetrics =
    status === 'sent' && !!sentAt && schedulingType !== 'notification'
  const showTags =
    (tags?.length ?? 0) > 0 || allowedActions?.includes('updatePostTags')

  const threadCount = useMetadataThreadCount(metadata)
  const threads = metadata && 'thread' in metadata ? metadata.thread : undefined
  const threadEntries = getEntriesWithKeys(threads ?? [], (thread) =>
    JSON.stringify({
      text: thread.text,
      assetIds: readFragment(PostMediaAsset_Asset, thread.assets ?? []).map(
        (asset) => asset.id,
      ),
    }),
  )
  const firstComment = useMetadataFirstComment(metadata)
  const geolocation = useMetadataGeolocation(metadata)
  const title = useMetadataTitle(metadata)

  const annotations = readFragment(
    PostCardContent_Annotation,
    metadata?.annotations ?? [],
  )

  return (
    <Flex
      as="article"
      direction="column"
      align="stretch"
      className={styles.body}
    >
      <BodySection
        aria-label="Post Content"
        className={styles.postContentSection}
      >
        {threadCount <= 1 ? (
          <PostContent
            assets={assets}
            text={text}
            title={title}
            type={metadata?.type}
            annotations={annotations}
            expandText={expandText === 'true'}
            includeMedia={false}
          />
        ) : (
          <Flex direction="column" gap="md">
            {threadEntries.map(({ key, item: thread }, index) => {
              return (
                <PostContent
                  key={key}
                  threadIndex={index}
                  text={thread.text}
                  type={metadata?.type}
                  annotations={[]}
                  expandText={expandText === 'true' && index === 0}
                  assets={thread.assets ?? []}
                />
              )
            })}
          </Flex>
        )}
      </BodySection>
      {(geolocation || firstComment) && (
        <BodySection>
          <Flex direction="column" gap="xs">
            {geolocation && (
              <Text
                translate="no"
                icon={
                  <MapPinIcon data-testid="map-icon" aria-label="Location" />
                }
              >
                {geolocation?.text}
              </Text>
            )}
            {firstComment && (
              <Text
                translate="no"
                icon={
                  <CommentSquare1StIcon
                    data-testid="first-comment-icon"
                    aria-label="First comment"
                  />
                }
              >
                {firstComment}
              </Text>
            )}
          </Flex>
        </BodySection>
      )}
      {showTags && (
        <BodySection className={styles.tagsSection}>
          <PostCardTags />
        </BodySection>
      )}
      {threadCount <= 1 && (
        <div className={styles.mediaDock}>
          <PostCardAttachments />
          <PostMediaBentoBoxPreview assets={assets} />
        </div>
      )}
      {showMetrics && (
        <BodySection>
          <Metrics />
        </BodySection>
      )}
    </Flex>
  )
}

const Footer = ({
  onPostDeleted,
}: {
  onPostDeleted?: () => void
}): JSX.Element => {
  const { via } = usePostData()
  const isApiPost = via === 'api' || via === 'buffer'

  return (
    <Dialog.Footer className={styles.footer} showSeparator={false}>
      {!isApiPost && <PostedFrom />}
      {isApiPost && <PostCardAuthor />}
      <PostCardActions hideViewPostDetails onDeleted={onPostDeleted} />
    </Dialog.Footer>
  )
}

/**
 * Renders the full detail of a single post (header, body, footer) inside an
 * ancestor Popcorn `Dialog`. Used by the route-mounted `PostDetailsDialog`
 * (single-post view) and as the detail pane of the in-place `PostGroupDialog`.
 *
 * `post` is the raw (unmasked) `PostCard_Post` fragment ref.
 */
export const PostDetailsContent = ({
  post,
  onPostDeleted,
  focusNotesComposer = false,
}: {
  post: FragmentOf<typeof PostCard_Post>
  /** Called after the post is deleted from this view, e.g. to navigate away
   * from a route that identifies the post by id. */
  onPostDeleted?: () => void
  focusNotesComposer?: boolean
}): JSX.Element => {
  const postData = readFragment(PostCard_Post, post)
  const portalContainerId = `portal-container-${postData.id}`
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const { showPanel } = useCollaborationNotesPanel()
  const notesPostId = showPanel ? postData.id : null
  const unreadNotes = usePostUnreadNoteCount(notesPostId)
  const unreadMentions = usePostUnreadMentionCount(notesPostId)
  const [notesOpen, setNotesOpen] = useState(true)
  const notesPanelId = `post-details-notes-${postData.id}`
  const dismissWarn = useOptionalNotesDismissGuard()?.warn === true

  useEffect(
    function setInnerPortalContainer() {
      setContainer(document.getElementById(portalContainerId))
    },
    [portalContainerId],
  )

  useEffect(() => {
    if (dismissWarn || focusNotesComposer) setNotesOpen(true)
  }, [dismissWarn, focusNotesComposer])

  return (
    <PortalContainerProvider container={container ?? document.body}>
      <PostCardProvider post={post}>
        <div
          className={styles.card}
          data-post-id={postData.id}
          data-testid="expanded-post-card"
        >
          <Header>
            {showPanel && (
              <IconButton
                label={getNotesIndicatorLabel(unreadNotes)}
                tooltip={getNotesUnreadTooltip(unreadNotes, unreadMentions)}
                aria-controls={notesPanelId}
                aria-expanded={notesOpen}
                aria-pressed={notesOpen}
                onClick={(): void => setNotesOpen((open) => !open)}
                size="medium"
                type="button"
                variant="tertiary"
              >
                <NotesIndicator unreadCount={unreadNotes}>
                  <NoteBlankIcon />
                </NotesIndicator>
              </IconButton>
            )}
          </Header>
          <div className={styles.notices} data-testid="post-details-notices">
            <PostDetailsConnectionNotice />
            <PostCardProcessingNotice />
          </div>
          <div
            className={styles.main}
            data-testid="post-details-middle"
            data-notes-open={showPanel && notesOpen}
          >
            <Body />
            {showPanel && (
              <PostDetailsNotes
                postId={postData.id}
                channelId={postData.channel?.id}
                allowedActions={postData.allowedActions}
                id={notesPanelId}
                hidden={!notesOpen}
                focusComposer={focusNotesComposer}
              />
            )}
          </div>
          <Footer onPostDeleted={onPostDeleted} />
        </div>
        <div id={portalContainerId} style={{ position: 'absolute' }} />
      </PostCardProvider>
    </PortalContainerProvider>
  )
}
